import React, { useEffect, useRef, useState } from 'react'
import Swal from 'sweetalert2';
import SideBar from '../Sidebar/SideBar';
import FormDetailsApi from '../../Api/FormDetailsApi';
import AccountManagerApi from '../../Api/AccountManager';
import UserAuthenticationApi from '../../Api/UserAuthenticationApi';

const UserAuthentication = () => {
    const [selectedModules, setSelectedModules] = useState({});

    const [accountManagers, setAccountManagers] = useState([]);
    const [selectedManager, setSelectedManager] = useState(null);
    const [accountSearch, setAccountSearch] = useState("");
    const [showManagerList, setShowManagerList] = useState(false);
    const [userAuthRecords, setUserAuthRecords] = useState([]);
    const [highlightIndex, setHighlightIndex] = useState(0);
    const filteredManagers = accountManagers.filter((mgr) => {
        const label = `${mgr.name || ""} ${mgr.Username || ""}`.toLowerCase();
        return label.includes(accountSearch.toLowerCase());
    });
    const descriptionText = accountSearch || "Form Data";
    const [formList, setFormList] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const loadForms = async () => {
            try {
                const response = await FormDetailsApi.GetAllFormDetails();
                if (response?.StatusCode === 200) {
                    setFormList(response.Result || []);
                }
            } catch (err) {
                console.error("Failed to load form details", err);
            }
        };
        loadForms();
    }, []);

    useEffect(() => {
        const loadAccountManagers = async () => {
            try {
                const response = await AccountManagerApi.GetAllAccountManager();
                if (response?.StatusCode === 200) {
                    setAccountManagers(response.Result || []);
                }
            } catch (err) {
                console.error("Failed to load account managers", err);
            }
        };
        loadAccountManagers();
    }, []);

    useEffect(() => {
        const loadUserAuth = async () => {
            try {
                const response = await UserAuthenticationApi.getAll();
                if (response?.StatusCode === 200) {
                    setUserAuthRecords(response.Result || []);
                }
            } catch (err) {
                console.error("Failed to load user authentication data", err);
            }
        };
        loadUserAuth();
    }, []);

    useEffect(() => {
        // Reset highlight when the dropdown closes or the list changes
        if (!showManagerList) {
            setHighlightIndex(0);
            return;
        }
        setHighlightIndex((prev) => (prev < filteredManagers.length ? prev : 0));
    }, [showManagerList, filteredManagers.length]);

    useEffect(() => {
        // Initialize selection map whenever data loads (all deselected by default)
        const mapped = {};
        formList.forEach((item) => {
            mapped[item.Id] = false;
        });
        setSelectedModules(mapped);
    }, [formList]);

    // When user changes, pre-select modules already stored for that user
    useEffect(() => {
        if (!formList.length) return;
        const userCandidates = [];
        if (selectedManager?.id) userCandidates.push(String(selectedManager.id).toLowerCase());

        if (!userCandidates.length) {
            // Reset to all false while keeping existing keys
            setSelectedModules((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((k) => (next[k] = false));
                return next;
            });
            return;
        }

        const matchedForms = userAuthRecords.filter((rec) => {
            const userVal = (rec.User || "").toLowerCase();
            return userCandidates.includes(userVal);
        });

        setSelectedModules((prev) => {
            const next = { ...prev };
            // default false
            Object.keys(next).forEach((k) => (next[k] = false));
            matchedForms.forEach((rec) => {
                const match = formList.find((f) => f.FormName === rec.FormName);
                if (match) {
                    next[match.Id] = true;
                }
            });
            return next;
        });
    }, [selectedManager, accountSearch, userAuthRecords, formList]);

    const allSelected = formList.length > 0 && formList.every((item) => selectedModules[item.Id]);

    const submitAuth = async (mode) => {
        const userId = selectedManager?.id;
        if (!userId) {
            Swal.fire({ title: 'Validation', text: 'Please select an Account Manager (uses ID).', icon: 'warning', width: '340px' });
            return;
        }
        const selectedItems = formList.filter((item) => selectedModules[item.Id]);
        if (!selectedItems.length) {
            Swal.fire({ title: 'Validation', text: 'Please select at least one module.', icon: 'warning', width: '320px' });
            return;
        }

        const invalidItems = selectedItems.filter(
            (item) => !item.FormName || !item.FormLink
        );
        if (invalidItems.length) {
            Swal.fire({
                title: 'Validation',
                text: 'Selected modules are missing FormName or FormLink. Please fix in Form Details first.',
                icon: 'warning',
                width: '360px',
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                UserId: userId,
                List: selectedItems.map((item) => ({
                    FormName: item.FormName,
                    FormLink: item.FormLink,
                })),
            };

            const res = await UserAuthenticationApi.upsert(payload);

            if (res?.StatusCode && res.StatusCode >= 400) {
                Swal.fire({
                    title: 'Error',
                    text: res?.ErrorMessages || 'Failed to save authentication.',
                    icon: 'error',
                    width: '360px',
                });
            } else {
                Swal.fire({
                    title: mode === 'save' ? 'Saved' : 'Updated',
                    text: `Authentication ${mode === 'save' ? 'saved' : 'updated'} successfully.`,
                    icon: 'success',
                    width: '320px',
                });
                const refreshed = await UserAuthenticationApi.getAll();
                if (refreshed?.StatusCode === 200) {
                    setUserAuthRecords(refreshed.Result || []);
                }
            }
        } catch (err) {
            console.error(err);
            Swal.fire({ title: 'Error', text: 'Failed to save authentication.', icon: 'error', width: '320px' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSelectAllModules = () => {
        const next = !allSelected;
        const mapped = {};
        formList.forEach((item) => {
            mapped[item.Id] = next;
        });
        setSelectedModules(mapped);
    };

    const handleModuleToggle = (id) => {
        setSelectedModules((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    //***************{Enter Btn Click}***************//
    const input1Ref = useRef(null);
    const input3Ref = useRef(null);
    const input4Ref = useRef(null);

    const handleKeyPress = (event, nextInputRef) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            nextInputRef.current.focus();
        }
    };

    return (
        <div className='Flex-general-client-sidebar'>
            <div className='sidebar-gen-width'>
                <SideBar />
            </div>
            <div className='Main-general-Client'>
                <div className='header-general'>
                    <h2 className='general-card-title'>User Authentication</h2>
                </div>
                <div className="control_radius">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="flex-user-authentication">
                                <div className="search_panel_hd1 left-input-panels" style={{ width: "70%", display: "flex", gap: "12px" }}>
                                    <div className="search_panel_left_side" style={{ width: "50%" }}>
                                        <div className="input-box-mo1" style={{ marginLeft: "10px" }}>
                                            <div className="dropdown-container-schedule">
                                                <input
                                                    className="modify_im_input mode_category"
                                                    ref={input1Ref}
                                                    placeholder="Select account manager"
                                                    value={accountSearch}
                                                    onChange={(e) => {
                                                        setAccountSearch(e.target.value);
                                                        setShowManagerList(true);
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                                                            event.preventDefault();
                                                            const len = filteredManagers.length;
                                                            if (!len) return;
                                                            setShowManagerList(true);
                                                            setHighlightIndex((prev) => {
                                                                if (event.key === 'ArrowDown') {
                                                                    return (prev + 1) % len;
                                                                }
                                                                return (prev - 1 + len) % len;
                                                            });
                                                            return;
                                                        }
                                                        if (event.key === 'Enter' && showManagerList && filteredManagers.length) {
                                                            event.preventDefault();
                                                            const mgr = filteredManagers[highlightIndex] || filteredManagers[0];
                                                            const label = mgr.name || mgr.Username || "Account Manager";
                                                            setAccountSearch(label);
                                                            setSelectedManager(mgr);
                                                            setShowManagerList(false);
                                                            return;
                                                        }
                                                        if (event.key === 'Escape') {
                                                            setShowManagerList(false);
                                                            return;
                                                        }
                                                        handleKeyPress(event, input3Ref);
                                                    }}
                                                    onFocus={() => setShowManagerList(true)}
                                                    onBlur={() => setTimeout(() => setShowManagerList(false), 150)}
                                                />
                                                {showManagerList && (
                                                    <div className="dropdown-menu-schedule" style={{ display: "block" }}>
                                                        {filteredManagers.length ? (
                                                            filteredManagers.map((mgr, idx) => {
                                                                const label = mgr.name || mgr.Username || "Account Manager";
                                                                return (
                                                                    <label
                                                                        key={mgr.id || label}
                                                                        style={highlightIndex === idx ? { backgroundColor: "#eef2ff" } : undefined}
                                                                        onMouseDown={() => {
                                                                            // Display name in the input; store manager for id-based save
                                                                            setAccountSearch(label);
                                                                            setSelectedManager(mgr);
                                                                            setShowManagerList(false);
                                                                        }}
                                                                    >
                                                                        {label}
                                                                    </label>
                                                                );
                                                            })
                                                        ) : (
                                                            <label>No matches</label>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <label className="modify_lbl_text3" style={{ backgroundColor: "transparent" }}>Account Manager</label>
                                        </div>
                                    </div>

                                </div>
                                <div className="ButtonTabSave right-buttons-panels">
                                    <button
                                        id="SaveBtnMain"
                                        className={`save-button-main-save hovered buttons-width-crud`}
                                        disabled={isSubmitting}
                                        onClick={() => submitAuth('save')}
                                    >
                                        {isSubmitting ? 'Saving...' : 'Save'}
                                    </button>
                                    {/* <button
                                        id="UpdateBtnMain"
                                        className={`update-button-main-save hovered buttons-width-crud`}
                                        disabled={isSubmitting}
                                        onClick={() => submitAuth('update')}
                                    >
                                        {isSubmitting ? 'Updating...' : 'Update'}
                                    </button> */}
                                </div>
                            </div>
                            <div className="col-sm-12" style={{ marginTop: "10px" }}>
                                <div className="table-resposive">
                                    <table id="tblTransactionList" className="table_hdo2 table-bordered1" frame="box">
                                        <thead textAlign="center" className="thead_scroll_col1">
                                            <tr className="">
                                                <th className="td_col5">
                                                    <div className="table_head_mmb ">
                                                        <div className="table_head_mmb1">S.No.</div>
                                                        {/* <div className="table_head_mmb2"><i class="fa-solid fa-sort-up t_icon1"></i><i class="fa-solid fa-sort-down t_icon2"></i></div> */}
                                                    </div>
                                                </th>
                                                <th className="td_col5">
                                                    <div className="table_head_mmb">
                                                        <div>
                                                            <input
                                                                type="checkbox"
                                                                className="check_box check-right"
                                                                checked={allSelected}
                                                                onChange={handleSelectAllModules}
                                                            />
                                                        </div>
                                                        <div className="table_head_mmbb1">Select All</div>
                                                    </div>
                                                </th>
                                                <th className="td_col5">
                                                    <div className="table_head_mmb">
                                                        <div className="table_head_mmb1">Module Name</div>
                                                    </div>
                                                </th>
                                                <th className="td_col5">
                                                    <div className="table_head_mmb">
                                                        <div className="table_head_mmb1">Authentication Details</div>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="tbody_body_m1">
                                            {formList.map((item, idx) => (
                                                <tr className="table_tbody_tr1" key={item.Id}>
                                                    <td className="td_col5">{idx + 1}</td>
                                                    <td className="td_col5" style={{ textAlign: "center" }}>
                                                        <input
                                                            type="checkbox"
                                                            className="check_box"
                                                            checked={!!selectedModules[item.Id]}
                                                            onChange={() => handleModuleToggle(item.Id)}
                                                        />
                                                    </td>
                                                    <td className="td_col5" style={{ textAlign: "center" }}>
                                                        {item.FormName || "Unnamed Form"}
                                                    </td>
                                                    <td className="td_col6" style={{ textAlign: "center" }}>
                                                        <a href={item.FormLink} target="_blank" rel="noreferrer">
                                                            {item.FormLink || "No link"}
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                            {!formList.length && (
                                                <tr>
                                                    <td className="td_col5" colSpan={4} style={{ textAlign: "center" }}>
                                                        No form details found.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >
        </div >
    )
}

export default UserAuthentication



